<template>
    <transition
        name="auth"
        mode="out-in"
    >
        <RegistrationForm
            v-if="isRegistration"
            ref="registration"
            :errors="registrationErrors"
            :disabled="registrationDisabled"
            :hide-inputs-if-logged="registrationHideInputsIfLogged"
            :hide-back-button
            @submit="$emit('register', $event)"
            @switch-to-login="switchToLogin"
            @page-email="emit('pageEmail', $event)"
        >
            <template #title>
                <slot name="registration-title" />
            </template>

            <template #description>
                <slot name="registration-description" />
            </template>

            <template #contact>
                <slot name="registration-contact" />
            </template>

            <template #submit>
                <slot name="registration-submit" />
            </template>
        </RegistrationForm>

        <LoginForm
            v-else
            ref="login"
            :hide-back-button
            @login="onSubmitLogin"
            @error="$emit('loginError', $event)"
            @switch-to-registration="switchToRegistration"
            @page-email="emit('pageEmail', $event)"
        >
            <template #title>
                <slot name="login-title" />

                <div
                    v-if="hasAccount"
                    class="existing-account-warning"
                >
                    It looks like you already have an account. Sign in to
                    continue.
                </div>
            </template>
        </LoginForm>
    </transition>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watch } from 'vue'

import type { ErrorCollectionWithTraces } from '@/helpers/errors'

import type {
    LoginResponseFailureFragment,
    LoginResponseSuccessFragment,
} from '@/graphql/api'

import LoginForm from './LoginForm.vue'
import RegistrationForm from './RegistrationForm.vue'
import { useRegistrationResponse } from './useRegistrationResponse'

export type { Redirect } from './useRegistrationResponse'

// Manual means user clicked "Sign in" manually, auto means user was existing and we showed him "Sign in" automatically.
export type LoginType = 'manual' | 'auto'

const { startWithRegistration = true } = defineProps<{
    startWithRegistration?: boolean
    registrationErrors: ErrorCollectionWithTraces
    registrationDisabled?: boolean
    registrationHideInputsIfLogged?: boolean
    hideBackButton?: boolean
}>()

const emit = defineEmits<{
    login: [v: LoginResponseSuccessFragment, LoginType]
    register: [successCallback: () => void]
    loginError: [v: LoginResponseFailureFragment]
    pageEmail: [boolean]
}>()

const isRegistration = ref(startWithRegistration)
const switchToLogin = () => {
    isRegistration.value = false
}

watch(isRegistration, () => {
    emit('pageEmail', false)
})

const switchToRegistration = () => {
    isRegistration.value = true
}

const { hasAccount, handleRegistrationResponse, redirectPending } =
    useRegistrationResponse(switchToLogin)

const componentRegistration = useTemplateRef('registration')
const componentLogin = useTemplateRef('login')

defineExpose({
    handleRegistrationResponse,
    switchToLogin,
    goToPageSso: () =>
        componentRegistration.value?.goToPageSso() ||
        componentLogin.value?.goToPageSso(),
})

const onSubmitLogin = (login: LoginResponseSuccessFragment) => {
    emit('login', login, hasAccount.value ? 'auto' : 'manual')

    redirectPending()
}
</script>

<style scoped>
.existing-account-warning {
    font-size: 14px;
    color: rgb(102, 60, 0);
    text-align: center;
    background-color: rgb(255, 244, 229);
    padding: 12px 20px;
    border-radius: 8px;
    border: 1px solid rgb(255, 226, 185);
    margin: 10px auto;
    width: 100%;
    font-weight: 500;
}

.auth-enter-active,
.auth-leave-active {
    transition: opacity 0.45s ease;
}

.auth-enter-from,
.auth-leave-to {
    opacity: 0;
}
</style>
